import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { Row, Col } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/client'
import dynamic from 'next/dynamic'

import teamPlaceholder from '@assets/images/placeholder/team-placeholder.jpg'
import Error from '@shared/components/error'
import AuctionPlayer from '@shared/components/auction/auctionPlayer'
import AuctionFilter from '@shared/components/auction/auctionFilter'
import { articleLoader } from '@shared/libs/allLoader'
import { GET_LIST_AUCTION_PLAYER } from '@graphql/ipl-auction/ipl-auction-list-players'
import { debounce, getImgURL, isBottomReached, numDifferentiation } from '@shared/utils'
import MyImage from '@shared/components/myImage'
import styles from '../teams/style.module.scss'
import { IPL_AUCTION_TEAMS_BY_ID } from '@graphql/ipl-auction/ipl-auction.query'

const Ads = dynamic(() => import('@shared/components/ads'))

const AuctionCompleted = ({ seoData, players, category }) => {
  const router = useRouter()
  const [playersData, setPlayersData] = useState(players)
  const [loading, setLoading] = useState(false)
  const [payload, setPayload] = useState(makePayload(router.query))
  const isLoading = useRef(false)
  const total = useRef(payload.nLimit)
  const isReachedBottom = useRef(false)
  const [teamDetail, setTeamDetail] = useState(category?.teamDetail)

  const [fetchMore, { data }] = useLazyQuery(GET_LIST_AUCTION_PLAYER, { variables: { input: payload } })
  const [fetchTeam] = useLazyQuery(IPL_AUCTION_TEAMS_BY_ID, {
    onCompleted: (d) => {
      setTeamDetail(d?.getAuctionTeamFront?.oData)
    }
  })

  useEffect(() => {
    if (data?.listAuctionPlayerFront) {
      if (isReachedBottom.current) {
        setPlayersData({
          ...playersData,
          aData: [...playersData?.aData, ...data?.listAuctionPlayerFront?.aData]
        })
      } else {
        setPlayersData({
          ...playersData,
          aData: [...data?.listAuctionPlayerFront?.aData]
        })
      }
      total.current = data?.listAuctionPlayerFront?.aData?.length || 0
      isReachedBottom.current = false
    }
  }, [data])

  useEffect(() => {
    isLoading.current = false
    setLoading(false)
    isBottomReached(playersData?.aData[playersData?.aData.length - 1]?._id, isReached)
  }, [playersData?.aData])

  async function isReached(reach) {
    if (reach && !isReachedBottom.current && !isLoading.current && total.current !== 0) {
      isReachedBottom.current = true
      isLoading.current = true
      setLoading(true)
      setPayload((p) => {
        const n = { ...p, nSkip: p?.nSkip + total.current }
        fetchMore({
          variables: { input: n }
        })
        return n
      })
    }
  }

  const handleSearch = debounce(({ target }) => {
    // console.log(target?.value)
    // router.push(
    //   {
    //     query: { ...router?.query, search: target?.value }
    //   },
    //   undefined,
    //   { shallow: true }
    // )
    handlePayloadChange({ ...payload, sSearch: target?.value })
  })

  function handleFilter(params) {
    // router.push(
    //   {
    //     query: { ...router?.query, ...params }
    //   },
    //   undefined,
    //   { shallow: true }
    // )
    if (params?.iTeamId && category?.data?.oAuctionSeries?._id) {
      fetchTeam({ variables: { input: { iTeamId: params?.iTeamId, iAuctionSeriesId: category?.data?.oAuctionSeries?._id } } })
    }
    handlePayloadChange(params ? { ...payload, ...params } : null)
  }

  function handlePayloadChange(query) {
    const p = makePayload(query)
    setPayload(p)
    fetchMore({ variables: { input: p } })
  }

  function makePayload(query) {
    return {
      nLimit: 10,
      nSkip: 0,
      sSearch: query?.sSearch || null,
      bCappedPlayer: query?.bCappedPlayer,
      sPlayingRole: query?.sPlayingRole || null,
      sSortBy: query?.sort ? 'nSoldPrice' : null,
      nOrder: query?.sort === 'Final Price 0-9' ? 1 : query?.sort === 'Final Price 9-0' ? -1 : null,
      eAuctionStatus: query?.eAuctionStatus || null,
      iTeamId: query?.iTeamId || null,
      iCountryId: query?.iCountryId || null,
      iPrimaryTeam: query?.iCountryId || null,
      iSeriesId: category?.data?.oAuctionSeries?._id
    }
  }

  return (
    <>
      <AuctionFilter handleSearch={handleSearch} handleFilter={handleFilter} category={category} payload={payload} />
      {payload.iTeamId && teamDetail && (
        <div
          className={`${styles.team} br-lg overflow-hidden position-relative my-3`}
          style={{ backgroundColor: `#${teamDetail?.sColorCode}` }}
        >
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
            <div className={`${styles.teamName} d-flex align-items-center big-text`}>
              <div className={`${styles.teamLogo} br-sm overflow-hidden`}>
                <MyImage
                  src={teamDetail.oTeam?.oImg?.sUrl ? getImgURL(teamDetail.oTeam?.oImg?.sUrl) : teamPlaceholder}
                  alt={teamDetail.oTeam?.sAbbr || 'team'}
                  width={120}
                  height={120}
                  sizes="(max-width: 768px) 40px"
                />
              </div>
              {teamDetail.oTeam?.sTitle || teamDetail.oTeam?.sAbbr}
            </div>
          </div>
          <div className={`${styles.info} ${styles.infoLage} d-flex align-items-center justify-content-around text-center`}>
            <div>
              <p className="mb-0">Total Players</p>
              <h4 className="fw-bold mb-0">{`${teamDetail.nPlayerCount}/25`}</h4>
            </div>
            <div>
              <p className="mb-0">Overseas Players</p>
              <h4 className="fw-bold mb-0">{teamDetail.nOverseaPlayerCount}/8</h4>
            </div>
            <div>
              <p className="mb-0">Purse Spent</p>
              <h4 className="fw-bold mb-0">{numDifferentiation(teamDetail.nTotalSpent)}</h4>
            </div>
            <div>
              <p className="mb-0">Purse Left</p>
              <h4 className="fw-bold mb-0">{numDifferentiation(teamDetail.nPurseLeft)}</h4>
            </div>
          </div>
        </div>
      )}
      <Row className="gx-3">
        {playersData?.aData?.map((player, index) => {
          return (
            <Col key={player?._id + index} lg={6} className="mb-3">
              <AuctionPlayer player={player} slug={category?.data?.sSlug} />
              {(index + 1) % 8 === 0 && (
                <Ads
                  id={`div-completed-${index}-ad-mob-23148549803-1732886876-0`}
                  adIdDesktop="Crictracker_New_Mobile_SP_MID_320x100"
                  dimensionDesktop={[320, 100]}
                  slotId="23148549803"
                  hideOn="desktop"
                  className={'text-center mt-3'}
                />
              )}
            </Col>
          )
        })}
      </Row>
      {loading && articleLoader(['g', 's'])}
    </>
  )
}

AuctionCompleted.propTypes = {
  data: PropTypes.object,
  seoData: PropTypes.object,
  players: PropTypes.object,
  category: PropTypes.object
}

export default Error(AuctionCompleted)
